import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { z } from "zod";

import ErrorIcon from "assets/images/consent-alert-icon.svg";
import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { Button } from "components/ui";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientConsent } from "features/patients/hooks/usePatientConsent";
import { useSavePatientConsent } from "features/patients/hooks/useSavePatientConsent";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./Consent.module.css";

const formSchema = z.object({
  sms: z.boolean(),
  email: z.boolean(),
  phone: z.boolean(),
  post: z.boolean(),
  aiScribe: z.boolean(),
  healthlink: z.boolean(),
});

const FIELDS = [
  { name: "sms", label: "Sms" },
  { name: "email", label: "Email" },
  { name: "phone", label: "Phone" },
  { name: "aiScribe", label: "Ai Scribe" },
  { name: "post", label: "Post" },
  { name: "healthlink", label: "Healthlink" },
];

const Consent = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const patientId = useCurrentPatientId();
  const { data: consent, refetch } = usePatientConsent(patientId);
  const saveConsent = useSavePatientConsent();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sms: false,
      email: false,
      phone: false,
      post: false,
      aiScribe: false,
      healthlink: false,
    },
  });

  useEffect(() => {
    if (consent) {
      form.reset({
        sms: consent.sms ?? false,
        email: consent.email ?? false,
        phone: consent.phone ?? false,
        post: consent.post ?? false,
        aiScribe: consent.aiScribe ?? false,
        healthlink: consent.healthlink ?? false,
      });
    }
  }, [consent]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (data) => {
    saveConsent.mutate(
      {
        id: consent?.id || null,
        patientId: Number(patientId),
        ...data,
      },
      {
        onSuccess: () => {
          toast.success("Consent saved successfully");
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error("Failed to save consent:", error);
          toast.error("Failed to save consent. Please try again.");
        },
      }
    );
  };

  const getIcon = (fieldName) => {
    if (!consent) return <img src={ErrorIcon} alt="" className={s.errorIcon} />;

    return consent[fieldName] ? (
      <AiOutlineCheckCircle className={s.checkIcon} />
    ) : (
      <img src={ErrorIcon} alt="" className={s.errorIcon} />
    );
  };

  return (
    <>
      <CardWithTitle
        title="Consent"
        endIcon={Pencil}
        buttonTitle="Edit"
        className={s.editButton}
        onButtonClick={() => setIsModalOpen(true)}
      >
        <ul className={s.listContainer}>
          {FIELDS.map((field) => (
            <li key={field.name} className={s.listItemContainer}>
              <p className={s.listItemTitle}>{field.label}:</p>
              {getIcon(field.name)}
            </li>
          ))}
        </ul>
      </CardWithTitle>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Consent"
        width={500}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave, onInvalidSubmit("Consent"))}
            className={s.formContainer}
          >
            {FIELDS.map((field) => (
              <div key={field.name} className={s.inputWrapper}>
                <p className={s.rowTitle}>{field.label}:</p>
                <Controller
                  name={field.name}
                  control={form.control}
                  render={({ field: controllerField }) => (
                    <CustomCheckbox
                      checked={controllerField.value}
                      handleChange={(e) =>
                        controllerField.onChange(e.target.checked)
                      }
                    />
                  )}
                />
              </div>
            ))}
          </form>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={form.handleSubmit(handleSave, onInvalidSubmit("Consent"))}
            disabled={saveConsent.isPending}
          >
            {saveConsent.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export { Consent };
